import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AbsenceTypes = () => {
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    category: 'Holiday',
    name: '',
    code: '',
    days_deducted: 1
  });

  const categories = ['Holiday', 'Sick', 'Absent', 'Other leave'];

  const fetchAbsenceTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/absence-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAbsenceTypes(data);
    } catch (error) {
      toast.error('Failed to load absence types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAbsenceTypes();
  }, []);

  const handleCreate = async () => {
    if (!formData.name || !formData.code) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/absence-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        throw new Error('Failed to create absence type');
      }

      toast.success('Absence type created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchAbsenceTypes();
    } catch (error) {
      toast.error('Failed to create absence type');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this absence type?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/absence-types/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error('Failed to delete');
      }

      toast.success('Absence type deleted successfully');
      fetchAbsenceTypes();
    } catch (error) {
      toast.error('Failed to delete absence type');
    }
  };

  const resetForm = () => {
    setFormData({
      category: 'Holiday',
      name: '',
      code: '',
      days_deducted: 1
    });
  };

  const groupedTypes = absenceTypes.reduce((acc, type) => {
    if (!acc[type.category]) {
      acc[type.category] = [];
    }
    acc[type.category].push(type);
    return acc;
  }, {});

  if (loading) {
    return <div className="text-center py-20 text-teal-600">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 overflow-x-hidden" data-testid="absence-types-container">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-emerald-900">Absence Types</h3>
          <p className="text-sm sm:text-base text-emerald-600 mt-1">Manage absence categories and their leave deductions</p>
        </div>
        <Button
          data-testid="create-absence-type-button"
          onClick={() => setShowCreateDialog(true)}
          className="bg-teal-500 hover:bg-teal-600 w-full sm:w-auto"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Absence Type
        </Button>
      </div>

      {/* Absence Types by Category */}
      <div className="space-y-6">
        {categories.map((category) => (
          <div key={category} data-testid={`category-${category.toLowerCase().replace(' ', '-')}`}>
            <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${
                category === 'Holiday' ? 'bg-blue-500' :
                category === 'Sick' ? 'bg-red-500' :
                category === 'Absent' ? 'bg-amber-500' :
                'bg-purple-500'
              }`} />
              {category}
            </h4>
            
            {groupedTypes[category] && groupedTypes[category].length > 0 ? (
              <div className="space-y-2">
                {groupedTypes[category].map((type) => (
                  <div
                    key={type.id}
                    data-testid={`absence-type-${type.id}`}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-teal-400 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-semibold text-gray-800">{type.name}</p>
                        <p className="text-sm text-gray-600">Code: <span className="font-mono">{type.code}</span></p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Days Deducted</p>
                        <p className="font-bold text-gray-800">{type.days_deducted}</p>
                      </div>
                      <Button
                        data-testid={`delete-absence-type-${type.id}`}
                        onClick={() => handleDelete(type.id)}
                        variant="outline"
                        className="border-red-500 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-500">
                No absence types in this category
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-absence-type-dialog">
          <DialogHeader>
            <DialogTitle>Add Absence Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Category *</label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="create-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Name *</label>
              <Input
                data-testid="create-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Annual Leave, Maternity Leave"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Code *</label>
              <Input
                data-testid="create-code-input"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., AL, ML, SL"
                maxLength={5}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Days Deducted from Annual Leave *</label>
              <Input
                data-testid="create-days-input"
                type="number"
                step="0.5"
                min="0"
                value={formData.days_deducted}
                onChange={(e) => setFormData({ ...formData, days_deducted: parseFloat(e.target.value) })}
              />
              <p className="text-xs text-gray-500 mt-1">Set to 0 if this absence type should not deduct from annual leave</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                data-testid="create-cancel-button"
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="create-submit-button"
                onClick={handleCreate}
                className="flex-1 bg-teal-500 hover:bg-teal-600"
              >
                Create Absence Type
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AbsenceTypes;
